import { useState, useEffect, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import fileDownload from 'js-file-download';
import moment from 'moment';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import { useMediaQuery } from '@mui/material';
import TicketView from '@IssueConsoleViews/TicketView';
import LabelClickUpMenu from '@AgileRepresentationalDisplay/LabelClickUpMenu';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import RegistryLinkViewService from '@Helpers/URLHelpers/WeboutService/views/RegistryLinkViewService';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './TicketCard.scss';

function TicketCard(props) {
    const [showTicketView, setShowTicketView] = useState(false);
    const [isPriorityEnable, setIsPriorityEnable] = useState(false);
    const [assigneeUser, setAssigneeUser] = useState({ userName: 'Unassigned', fullName: 'Unassigned' });
    const [labelRegistryLinkList, setLabelRegistryLinkList] = useState([]);
    const [anchorFieldPopup, setAnchorFieldPopup] = useState(null);
    const [fieldPopupText, setFieldPopupText] = useState('');
    const [hoverTimer, setHoverTimer] = useState(null);
    const [pdfDownloading, setPdfDownloading] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();
    const registryLinkViewService = new RegistryLinkViewService();
    const ticketService = new TicketService();
    const is0To992 = useMediaQuery('(max-width: 992px)');
    const [searchParams, setSearchParams] = useSearchParams();
    const className = 'ticketCard';

    useEffect(() => {
        renderData();
        return () => {
            if (hoverTimer) clearTimeout(hoverTimer);
        };
    }, [props]);

    const renderData = async () => {
        if (!!props.ticket?.assigneeUserId && props.ticket?.assigneeUserId !== 0) {
            let userObj = await userInfoService.getUserById(props.ticket?.assigneeUserId);
            userObj = JSON.parse(userObj.body);
            setAssigneeUser(userObj);
        }
        if (!!props.kaiziqResolveConnection) {
            let kaiziqResolveConnectionDetails = JSON.parse(props.kaiziqResolveConnection?.details);
            let settingsObj = kaiziqResolveConnectionDetails.settings;
            setIsPriorityEnable(settingsObj?.defaults?.priority ?? false);
        }
        await renderLabelRegistryLinkList();
    };

    const toggleShowTicketView = () => {
        const opening = !showTicketView;
        setShowTicketView(opening);
        const next = new URLSearchParams(searchParams);
        if (opening) {
            next.set('formId', props.kaiziqResolveConnection?.formId);
            next.set('ticketId', btoa(String(props.ticket?.id)));
        } else {
            next.delete('ticketId');
        }
        setSearchParams(next, { replace: true });
    };

    const handleCopyLink = (e) => {
        e.stopPropagation();
        const formId = props.kaiziqResolveConnection?.formId;
        const ticketId = btoa(String(props.ticket?.id));
        const ws = searchParams.get('ws') || '';
        const params = new URLSearchParams();
        if (ws) params.set('ws', ws);
        params.set('formId', formId);
        params.set('tab', 'responses');
        params.set('ticketId', ticketId);
        const url = `${window.location.origin}/#/forms?${params.toString()}`;
        navigator.clipboard.writeText(url).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        });
    };

    const handleFieldPopupOpen = (event, value) => {
        const timer = setTimeout(() => {
            setAnchorFieldPopup(event.currentTarget);
            setFieldPopupText(value);
        }, 500); // 500ms delay
        setHoverTimer(timer);
    };

    const handleFieldPopupClose = () => {
        if (hoverTimer) {
            clearTimeout(hoverTimer);
            setHoverTimer(null);
        }
        setAnchorFieldPopup(null);
    };

    const renderLabelRegistryLinkList = async () => {
        let loadLoops = 0, loadingError = false, errorException;
        for (loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let labelRegistryLinkListObj = await registryLinkViewService.getListByTargetTypeAndTargetIdAndRegistryType(
                    'ticket',
                    props?.ticket?.id,
                    'label'
                );
                labelRegistryLinkListObj = JSON.parse(labelRegistryLinkListObj?.body);
                setLabelRegistryLinkList(labelRegistryLinkListObj);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Label Registry Link List Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if (loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            throw new Error(errorException);
        }
        return true;
    };

    const handleDownloadPdf = async (event) => {
        event?.stopPropagation();
        if (pdfDownloading || !props?.ticket?.id) return;
        setPdfDownloading(true);
        try {
            const pdfResponse = await ticketService.getTicketAsPdf('agent', props.ticket.id);

            let parsedResponse = pdfResponse;
            if (typeof parsedResponse === 'string') {
                try {
                    parsedResponse = JSON.parse(parsedResponse);
                } catch (e) {
                    // Keep raw value for backward compatibility where API returns base64 directly.
                }
            }

            let responseBody = parsedResponse?.body;
            if (typeof responseBody === 'string') {
                try {
                    responseBody = JSON.parse(responseBody);
                } catch (e) {
                    // Ignore and continue with fallback extraction.
                }
            }

            let fileClob = responseBody?.fileClob ?? parsedResponse?.fileClob;
            if (typeof fileClob === 'string') {
                try {
                    fileClob = JSON.parse(fileClob);
                } catch (e) {
                    // Ignore and continue with fallback extraction.
                }
            }

            let fileBase64 =
                fileClob?.file ??
                responseBody?.file ??
                parsedResponse?.file ??
                (typeof pdfResponse === 'string' ? pdfResponse : null);

            if (!fileBase64 || typeof fileBase64 !== 'string') {
                throw new Error('No PDF data received');
            }

            fileBase64 = fileBase64.replace(/^data:application\/pdf;base64,/, '');
            const bytes = atob(fileBase64);
            const byteArray = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) byteArray[i] = bytes.charCodeAt(i);
            const fileType = fileClob?.fileType || 'application/pdf';
            const fileName = fileClob?.fileName || `ticket-${props?.ticket?.ticketId || props?.ticket?.id}.pdf`;
            const blob = new Blob([byteArray], { type: fileType });
            fileDownload(blob, fileName, fileType);
        } catch (err) {
            errorHandler.logError('TicketCard PDF Download', err);
        }
        setPdfDownloading(false);
    };

    const getStatusInfo = () => {
        const statusField = props?.adminFieldList?.find((field) => field.title === 'status');
        const statusOptions = statusField ? JSON.parse(statusField.options) : [];
        return statusOptions.find((option) => option.id === props?.ticket?.status) || {};
    };

    const renderLabelList = (list, maxCount) => {
        if (!list || list.length === 0) return <span className={className + '__noLabels'}>-</span>;

        return (
            <Box className={className + '__labelList'}>
                {list.slice(0, maxCount).map((label) => (
                    <LabelClickUpMenu key={label?.id} labelLink={label} />
                ))}
                {list.length > maxCount && (
                    <Tooltip
                        title={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {list.slice(maxCount).map((label) => (
                                    <LabelClickUpMenu key={label?.id} labelLink={label} />
                                ))}
                            </Box>
                        }
                        enterDelay={500}
                        slotProps={{
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backdropFilter: 'none',
                                        backgroundColor: 'var(--color-componentBackground)',
                                        color: 'var(--color-text)',
                                        border: '1px solid var(--color-darkBorder)',
                                        boxShadow: '0 12px 24px var(--color-shadow)',
                                        borderRadius: '8px',
                                        padding: '8px'
                                    }
                                }
                            }
                        }}
                    >
                        <Chip
                            size="small"
                            label={`+${list.length - maxCount}`}
                            className={className + '__moreLabels'}
                        />
                    </Tooltip>
                )}
            </Box>
        );
    };

    const statusInfo = getStatusInfo();

    // Mobile Card View
    if (is0To992) {
        return (
            <Box className={className + '__mobile'} onClick={toggleShowTicketView}>
                <Box className={className + '__mobile__header'}>
                    <Chip
                        label={props.ticket?.ticketId || 'N/A'}
                        size="small"
                        className={className + '__mobile__tag'}
                    />
                    <Box className={className + '__mobile__headerRight'}>
                        <Chip
                            label={statusInfo?.title || 'Unknown'}
                            size="small"
                            sx={{
                                backgroundColor: statusInfo?.color || '#5f6368',
                                color: '#fff',
                                fontWeight: 600
                            }}
                        />
                        <Tooltip title={pdfDownloading ? 'Downloading...' : 'Download PDF'} placement="top">
                            <IconButton
                                size="small"
                                className={className + '__downloadBtn'}
                                onClick={handleDownloadPdf}
                                disabled={pdfDownloading}
                            >
                                {pdfDownloading ? <CircularProgress size={14} /> : <DownloadRoundedIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={linkCopied ? 'Link copied!' : 'Copy link'} placement="top" open={linkCopied || undefined}>
                            <IconButton size="small" className={className + '__downloadBtn'} onClick={handleCopyLink}>
                                <LinkRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                <Box className={className + '__mobile__title'}>{props.ticket?.ticketTitle || 'N/A'}</Box>
                <Box className={className + '__mobile__meta'}>
                    <span>{moment(props.ticket?.createTimeStamp).fromNow()}</span>
                    <span>•</span>
                    <span>{props.ticket?.createdByEmail}</span>
                </Box>
                <Box className={className + '__mobile__footer'}>
                    <Box className={className + '__mobile__assignee'}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            {assigneeUser?.userName?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <span>{assigneeUser?.userName !== 'Unassigned' ? `@${assigneeUser.userName}` : 'Unassigned'}</span>
                    </Box>
                    {renderLabelList(labelRegistryLinkList, 2)}
                </Box>
                {showTicketView && (
                    <TicketView
                        open={showTicketView}
                        closePopup={toggleShowTicketView}
                        ticketId={props.ticket?.id}
                        formId={props.kaiziqResolveConnection?.formId}
                        isPriorityEnable={isPriorityEnable}
                        reloadData={props.reloadData}
                    />
                )}
            </Box>
        );
    }

    // Desktop Table Row View
    return (
        <>
            <Box className={className} onClick={toggleShowTicketView}>
                <Box className={className + '__row'}>
                    {/* Tag */}
                    <Box
                        className={className + '__cell ' + className + '__cell--tag'}
                        onMouseEnter={(e) => handleFieldPopupOpen(e, props.ticket?.ticketId)}
                        onMouseLeave={handleFieldPopupClose}
                    >
                        <Chip
                            label={props.ticket?.ticketId || 'N/A'}
                            size="small"
                            className={className + '__tagChip'}
                        />
                    </Box>

                    {/* Title */}
                    <Box
                        className={`${className}__cell ${className}__cell--title`}
                        onMouseEnter={(e) => handleFieldPopupOpen(e, props.ticket?.ticketTitle)}
                        onMouseLeave={handleFieldPopupClose}
                    >
                        <span className={className + '__cellText'}>{props.ticket?.ticketTitle || 'N/A'}</span>
                    </Box>

                    {/* Status */}
                    <Box className={className + '__cell'}>
                        <Box className={className + '__status'}>
                            <Box
                                className={className + '__statusDot'}
                                sx={{ backgroundColor: statusInfo?.color || '#5f6368' }}
                            />
                            <span className={className + '__statusText'}>{statusInfo?.title || 'Unknown'}</span>
                        </Box>
                    </Box>

                    {/* Reported */}
                    <Box
                        className={className + '__cell'}
                        onMouseEnter={(e) => handleFieldPopupOpen(e, moment(props.ticket?.createTimeStamp).format('MMM DD, YYYY HH:mm'))}
                        onMouseLeave={handleFieldPopupClose}
                    >
                        <span className={className + '__cellText ' + className + '__cellText--grey'}>
                            {moment(props.ticket?.createTimeStamp).fromNow()}
                        </span>
                    </Box>

                    {/* Reporter */}
                    <Box
                        className={className + '__cell'}
                        onMouseEnter={(e) => handleFieldPopupOpen(e, props.ticket?.createdByEmail)}
                        onMouseLeave={handleFieldPopupClose}
                    >
                        <span className={className + '__cellText ' + className + '__cellText--grey'}>
                            {props.ticket?.createdByEmail}
                        </span>
                    </Box>

                    {/* Assignee */}
                    <Box
                        className={className + '__cell'}
                        onMouseEnter={(e) =>
                            handleFieldPopupOpen(
                                e,
                                assigneeUser?.userName !== 'Unassigned' ? `@${assigneeUser.userName}` : 'Unassigned'
                            )
                        }
                        onMouseLeave={handleFieldPopupClose}
                    >
                        <Box className={className + '__assignee'}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                {assigneeUser?.userName?.[0]?.toUpperCase() || 'U'}
                            </Avatar>
                            <span className={className + '__cellText ' + className + '__cellText--grey'}>
                                {assigneeUser?.userName !== 'Unassigned' ? `@${assigneeUser.userName}` : 'Unassigned'}
                            </span>
                        </Box>
                    </Box>

                    {/* Labels */}
                    <Box className={`${className}__cell ${className}__cell--label`}>
                        {renderLabelList(labelRegistryLinkList, 2)}
                    </Box>

                    {/* Custom Fields */}
                    {!!props.kaiziqResolveConnection?.details &&
                        JSON.parse(props.kaiziqResolveConnection?.details)?.fields?.client?.map((field, index) => {
                            if (field.type === 'information') return null;
                            const fieldValue = JSON.parse(props.ticket?.details || '{}')?.fields?.[field.id]?.value || '-';
                            return (
                                <Box
                                    key={field.id}
                                    className={className + '__cell'}
                                    onMouseEnter={(e) => handleFieldPopupOpen(e, fieldValue)}
                                    onMouseLeave={handleFieldPopupClose}
                                >
                                    <span className={className + '__cellText'}>{fieldValue}</span>
                                </Box>
                            );
                        })}
                    {/* Actions */}
                    <Box className={`${className}__cell ${className}__cell--actions`} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={linkCopied ? 'Link copied!' : 'Copy link'} placement="left" open={linkCopied || undefined}>
                            <IconButton size="small" className={className + '__downloadBtn'} onClick={handleCopyLink}>
                                <LinkRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={pdfDownloading ? 'Downloading...' : 'Download PDF'} placement="left">
                            <IconButton
                                size="small"
                                className={className + '__downloadBtn'}
                                onClick={handleDownloadPdf}
                                disabled={pdfDownloading}
                            >
                                {pdfDownloading ? <CircularProgress size={14} /> : <DownloadRoundedIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Popover
                    open={Boolean(anchorFieldPopup)}
                    anchorEl={anchorFieldPopup}
                    onClose={handleFieldPopupClose}
                    disableRestoreFocus
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                backdropFilter: 'none',
                                backgroundColor: 'var(--color-componentBackground)',
                                boxShadow: '0 12px 24px var(--color-shadow)',
                                border: '1px solid var(--color-darkBorder)'
                            }
                        }
                    }}
                    sx={{ pointerEvents: 'none' }}
                >
                    <Box className={className + '__popup'}>{fieldPopupText}</Box>
                </Popover>
            </Box>
            {showTicketView && (
                <TicketView
                    open={showTicketView}
                    closePopup={toggleShowTicketView}
                    ticketId={props.ticket?.id}
                    isPriorityEnable={isPriorityEnable}
                    reloadData={props.reloadData}
                />
            )}
        </>
    );
}

export default memo(TicketCard, arePropsEqual);

function arePropsEqual(oldProps, newProps) {
    return JSON.stringify(oldProps?.ticket) === JSON.stringify(newProps?.ticket);
}